import { useEffect, useRef } from 'react'

type Args = {
	setActiveId: (key?: string) => void
	target: 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
	targetId: string
}

export const useIntersectionObserver = ({ setActiveId, target, targetId }: Args) => {
	const headingElementsRef = useRef({})
	useEffect(() => {
		const callback = (headings: IntersectionObserverEntry[]) => {
			headingElementsRef.current = headings.reduce(
				(acc: Record<string, IntersectionObserverEntry>, current) => {
					acc[current.target.id] = current
					return acc
				},
				headingElementsRef.current
			)

			// Get all headings that are currently visible on the page
			const visibleHeadings: IntersectionObserverEntry[] = []
			Object.keys(headingElementsRef.current).forEach((key) => {
				const headingElement = (
					headingElementsRef.current as Record<string, IntersectionObserverEntry>
				)[key]
				if (headingElement.isIntersecting) visibleHeadings.push(headingElement)
			})

			const getIndexFromId = (id: string) =>
				headingElements.findIndex((heading) => heading.id === id)

			// If there is only one visible heading, set is as active
			if (visibleHeadings.length === 1) {
				setActiveId?.(visibleHeadings[0].target.id)
			}

			// If there is more than one visible heading,
			// choose the one that is closest to the top of the page
			if (visibleHeadings.length > 1) {
				const sortedVisibleHeadings = visibleHeadings.sort(
					(a, b) => getIndexFromId(a.target.id) - getIndexFromId(b.target.id)
				)
				setActiveId?.(sortedVisibleHeadings[0].target.id)
			}
		}

		const observer = new IntersectionObserver(callback, {
			rootMargin: '32px'
		})

		const headingElements = Array.from(document.querySelectorAll(`#${targetId} ${target}`))
		headingElements.forEach((element) => observer.observe(element))
		return () => observer.disconnect()
	}, [setActiveId, target, targetId])
}
